import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { apiFetch } from '../api/client'
import { qk } from '../api/queries'
import type { ActivityLog, ActivityResponse } from '../api/types'
import { useSession } from '../context/SessionContext'
import { Icon } from '../components/Icon'
import { formatDateTime } from '../components/format'
import { Badge, EmptyState, PageHeader, QueryState, type BadgeTone } from '../components/ui'

export const LIVE_INTERVAL_MS = 5_000

const RANGES = {
  '24h': { label: 'Last 24 hours', ms: 24 * 3600_000 },
  '7d': { label: 'Last 7 days', ms: 7 * 24 * 3600_000 },
  '30d': { label: 'Last 30 days', ms: 30 * 24 * 3600_000 },
} as const
type RangeKey = keyof typeof RANGES
type StatusFilter = 'all' | ActivityLog['status']

const STATUS_TONE: Record<ActivityLog['status'], BadgeTone> = {
  success: 'success',
  error: 'error',
  pending: 'warning',
}

function sinceFor(range: RangeKey): string {
  return new Date(Date.now() - RANGES[range].ms).toISOString()
}

function formatDuration(ms?: number | null): string {
  if (ms === null || ms === undefined) return '—'
  return ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(2)} s`
}

function ToolLogs() {
  const { workspaceId } = useSession()
  const [range, setRange] = useState<RangeKey>('30d')
  const [since, setSince] = useState(() => sinceFor('30d'))
  // ?tool=<name> deep link (e.g. from a Playground run) pre-fills the search.
  const [params] = useSearchParams()
  const [search, setSearch] = useState(() => params.get('tool') ?? '')
  const [status, setStatus] = useState<StatusFilter>('all')
  const [filterOpen, setFilterOpen] = useState(false)
  const [live, setLive] = useState(false)

  const q = useQuery({
    queryKey: qk.activity(workspaceId, since),
    queryFn: () =>
      apiFetch<ActivityResponse>(`/activity?limit=100&offset=0&since=${encodeURIComponent(since)}`),
    refetchInterval: live ? LIVE_INTERVAL_MS : false,
  })

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase()
    return (q.data?.activity ?? []).filter(
      (r) =>
        (status === 'all' || r.status === status) &&
        (!term || r.tool_name.toLowerCase().includes(term) || (r.server_type ?? '').toLowerCase().includes(term)),
    )
  }, [q.data, search, status])

  return (
    <>
      <div className="toolbar">
        <div className="input-icon toolbar-grow">
          <Icon name="search" size={14} />
          <input
            type="search"
            aria-label="Search logs"
            placeholder="Search by tool or toolkit"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <label className="chip-select">
          <Icon name="calendar" size={14} />
          <span className="sr-only">Date range</span>
          <select
            aria-label="Date range"
            value={range}
            onChange={(e) => {
              const r = e.target.value as RangeKey
              setRange(r)
              setSince(sinceFor(r))
            }}
          >
            {Object.entries(RANGES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </label>
        <div className="popover-anchor">
          <button
            type="button"
            className={'btn' + (status !== 'all' ? ' btn--active' : '')}
            aria-expanded={filterOpen}
            onClick={() => setFilterOpen((v) => !v)}
          >
            <Icon name="filter" size={14} /> Filter{status !== 'all' ? ': ' + status : ''}
          </button>
          {filterOpen && (
            <div className="popover" role="group" aria-label="Status filter">
              {(['all', 'success', 'error', 'pending'] as StatusFilter[]).map((s) => (
                <label key={s} className="check">
                  <input
                    type="radio"
                    name="log-status"
                    checked={status === s}
                    onChange={() => { setStatus(s); setFilterOpen(false) }}
                  />
                  {s === 'all' ? 'All statuses' : s[0].toUpperCase() + s.slice(1)}
                </label>
              ))}
            </div>
          )}
        </div>
        <label className={'live-toggle' + (live ? ' on' : '')}>
          <input
            type="checkbox"
            role="switch"
            aria-checked={live}
            checked={live}
            onChange={(e) => setLive(e.target.checked)}
          />
          <span className="live-dot" aria-hidden="true" /> Live
        </label>
      </div>

      <QueryState isLoading={q.isLoading} error={q.error} what="tool logs" onRetry={() => q.refetch()}>
        {(q.data?.activity ?? []).length === 0 ? (
          <EmptyState
            icon="list"
            title="No tool calls yet"
            body={`Nothing in the ${RANGES[range].label.toLowerCase()}. Every tool your agents execute through Symone is logged here with its status and duration.`}
            sampleHeaders={['Tool', 'Status', 'Duration', 'Client', 'Time']}
            sampleRows={[
              [<code>calendar_create_event</code>, <Badge tone="success">success</Badge>, '412 ms', 'claude-desktop', 'Oct 30, 14:02'],
              [<code>issues_list_open</code>, <Badge tone="error">error</Badge>, '1.08 s', 'sdk-python', 'Oct 30, 13:57'],
            ]}
          />
        ) : rows.length === 0 ? (
          <div className="state-panel state-panel--quiet"><p>No logs match these filters.</p></div>
        ) : (
          <div className="table-wrap">
            <table className="table table--dense">
              <thead>
                <tr>
                  <th>Tool</th>
                  <th>Status</th>
                  <th>Duration</th>
                  <th>Client</th>
                  <th>Time</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <code>{r.tool_name}</code>
                      {r.meta_tool && <div className="cell-sub">via {r.meta_tool}</div>}
                    </td>
                    <td><Badge tone={STATUS_TONE[r.status]}>{r.status}</Badge></td>
                    <td className="num">{formatDuration(r.latency_ms)}</td>
                    <td>{r.client?.display_name ?? '—'}</td>
                    <td className="nowrap">{formatDateTime(r.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {typeof q.data?.total === 'number' && q.data.total > rows.length && (
              <p className="table-foot">Showing {rows.length} of {q.data.total}</p>
            )}
          </div>
        )}
      </QueryState>
    </>
  )
}

function TriggerLogs() {
  return (
    <EmptyState
      icon="bolt"
      title="Trigger logs are coming soon"
      body="Each event delivered by a trigger (webhook or poll) will be logged here with its payload and delivery status. The gateway doesn’t expose a trigger-event log endpoint yet."
      sampleHeaders={['Trigger', 'Event', 'Status', 'Time']}
      sampleRows={[
        ['issues · webhook', <code>issue.created</code>, <Badge tone="success">delivered</Badge>, 'Oct 30, 14:05'],
      ]}
    />
  )
}

export function LogsView() {
  const [tab, setTab] = useState<'tool' | 'trigger'>('tool')
  return (
    <div className="page">
      <PageHeader title="Logs" description="Every tool call and trigger event in this workspace." />
      <div className="tabs" role="tablist" aria-label="Log type">
        <button type="button" role="tab" id="tab-tool" aria-selected={tab === 'tool'} aria-controls="panel-logs" className="tab" onClick={() => setTab('tool')}>
          Tool Logs
        </button>
        <button type="button" role="tab" id="tab-trigger" aria-selected={tab === 'trigger'} aria-controls="panel-logs" className="tab" onClick={() => setTab('trigger')}>
          Trigger Logs
        </button>
      </div>
      <div id="panel-logs" role="tabpanel" aria-labelledby={tab === 'tool' ? 'tab-tool' : 'tab-trigger'}>
        {tab === 'tool' ? <ToolLogs /> : <TriggerLogs />}
      </div>
    </div>
  )
}
